import { Formik, FormikState } from 'formik';
import { SaveReportNotificationSettings, UpdateReportNotificationSettings } from 'models/request';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAsync } from 'react-use';
import services from 'services';
import actions from 'state/actions';
import selectors from 'state/selectors';
import utils from 'utils';
import { generateCronExpression } from '../utils';
import FormReport from './FormReport';
import { FormValues, setInitialValues, validationLogic } from './reportUtils';
import TableReports from './TableReports';

const ReportsNotifications = () => {
  const t = utils.intl.useTranslator();
  const dispatch = useDispatch();

  const formMode = useSelector(selectors.configurator.getFormModeReports);
  const selectedRow = useSelector(selectors.configurator.getSelectedReportSettings);
  const idUser = useSelector(selectors.user.getIdUser);
  const reportList = useSelector(selectors.configurator.getListOfReportSettings);

  useAsync(async () => {
    try {
      if (!idUser) return;
      const { data } = await services.getScheduledNotificationsByUser(idUser);
      dispatch(actions.configurator.setListOfReportSettings(data));
    } catch (e) {
      console.error(e);
    }
  }, []);

  const onSubmit = async (
    values: FormValues,
    resetForm: (nextState?: Partial<FormikState<FormValues>> | undefined) => void,
  ) => {
    const { enabled, hour, minute, allDays, daysOfWeek, selectedReportFilter, selectedFolder, selectedReport } = values;
    if (!idUser) return;
    try {
      const bodyRequest: SaveReportNotificationSettings = {
        idUser: idUser,
        idUserReportSetting: Number(selectedReportFilter?.value),
        cronFrequency: generateCronExpression(hour, minute, allDays, daysOfWeek),
        enabled: enabled,
      };

      if (formMode === 'add') {
        const {
          data: { idSetting },
        } = await services.saveScheduledReport(bodyRequest);

        utils.app.notify('success', t('notification-setting-created'));

        dispatch(
          actions.configurator.setListOfReportSettings([
            {
              idFolder: Number(selectedFolder.value),
              folderName: selectedFolder.label,
              idReport: Number(selectedReport.value),
              reportName: selectedReport.label,
              filterName: selectedReportFilter?.label || '',
              idSetting: idSetting,
              ...bodyRequest,
            },
            ...reportList,
          ]),
        );
      }
      if (formMode === 'edit') {
        if (!selectedRow?.idSetting) return;
        const bodyRequestEdit: UpdateReportNotificationSettings = {
          idSetting: selectedRow?.idSetting,
          ...bodyRequest,
          idUserReportSetting: selectedRow?.idUserReportSetting,
        };

        await services.editScheduledReport(bodyRequestEdit);

        const newList = reportList.map((e) =>
          e.idSetting === bodyRequestEdit.idSetting
            ? {
                idFolder: Number(selectedFolder.value),
                folderName: selectedFolder.label,
                idReport: Number(selectedReport.value),
                reportName: selectedReport.label,
                filterName: selectedReportFilter.label,
                ...bodyRequestEdit,
              }
            : e,
        );

        dispatch(actions.configurator.setListOfReportSettings(newList));
        dispatch(actions.configurator.setSelectedReportSettings(null));
        utils.app.notify('success', t('notification-setting-edited'));
      }
    } catch (e) {
      console.error(e);
    } finally {
      dispatch(actions.configurator.setFormModeReport(null));
      resetForm();
    }
  };

  useAsync(async () => {
    if (formMode === 'edit' && selectedRow) {
      const { data } = await services.loadFiltersPreference(selectedRow?.idFolder, selectedRow?.idReport);
      dispatch(actions.userReportSettings.setOptionsFilterPreference(data));
      const selectedOption = data.find((el) => el.idUserReportSetting === selectedRow?.idUserReportSetting);
      if (selectedOption) {
        dispatch(actions.userReportSettings.setSelectedFilterPreference(selectedOption));
      }
    }
  }, [formMode]);
  return (
    <div>
      <h2>{t('Reports')}</h2>
      <TableReports />
      <Formik
        initialValues={setInitialValues(selectedRow, formMode, t)}
        onSubmit={(values, { resetForm }) => {
          onSubmit(values, resetForm);
        }}
        validationSchema={validationLogic(t, formMode)}
        enableReinitialize
      >
        {({ values, isValid, isValidating, dirty, setFieldValue, submitForm }) => (
          <div>
            {formMode && (
              <FormReport
                values={values}
                setFieldValue={setFieldValue}
                dirty={dirty}
                isValid={isValid}
                isValidating={isValidating}
                submitForm={submitForm}
              />
            )}
          </div>
        )}
      </Formik>
    </div>
  );
};
export default ReportsNotifications;
